import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Users2,
  Car,
  UserCog,
  Building2,
  Settings,
  Building,
  ClipboardList,
  Package,
  BarChart3,
  Receipt,
  TrendingUp,
  Trophy,
  BookOpen,
  AlertTriangle,
  Wallet,
  Layers,
  Target,
  ChevronDown,
  Menu,
  X,
  ShoppingCart,
  Truck,
  PackageX,
  FileText,
  History,
  Bell,
  Users,
  Wrench,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { isOperative } from "@/lib/api";
import { cn } from "@/lib/utils";

const NAV_TOP = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/alertas", label: "Alertas", icon: Bell, testId: "nav-alertas", perm: "alerts:view" },
  { to: "/ordenes-de-servicio", label: "Órdenes de servicio", icon: ClipboardList, testId: "nav-service-orders" },
  { to: "/inventario", label: "Inventario", icon: Package, testId: "nav-inventario" },
];

const NAV_BOTTOM = [
  { to: "/clientes", label: "Clientes", icon: Users2, testId: "nav-clientes" },
  { to: "/vehiculos", label: "Vehículos", icon: Car, testId: "nav-vehiculos" },
  { to: "/usuarios", label: "Usuarios", icon: UserCog, testId: "nav-usuarios" },
  { to: "/sucursales", label: "Sucursales", icon: Building2, testId: "nav-sucursales" },
  { to: "/sin-sucursal", label: "Sin sucursal asignada", icon: Building2, testId: "nav-sin-sucursal", ownerOnly: true },
  { to: "/flujos-servicio", label: "Flujos de servicio", icon: Building2, testId: "nav-flujos-servicio", perm: "service_flows:manage" },
  { to: "/configuracion", label: "Configuración", icon: Settings, testId: "nav-configuracion" },
];

const SUPER_ADMIN_NAV = [
  { to: "/tenants", label: "Tenants", icon: Building, testId: "nav-tenants" },
];

const REPORTS_NAV = [
  { to: "/reportes/ventas", label: "Ventas", icon: Receipt, testId: "nav-reportes-ventas", perm: "reports:view" },
  { to: "/reportes/flujo", label: "Flujo de caja", icon: TrendingUp, testId: "nav-reportes-flujo", perm: "reports:view" },
  { to: "/reportes/top-clientes", label: "Top clientes", icon: Trophy, testId: "nav-reportes-topclientes", perm: "reports:view" },
  { to: "/gastos", label: "Gastos", icon: Wallet, testId: "nav-gastos", perm: "expenses:view" },
  { to: "/gastos/presupuesto", label: "Presupuesto", icon: Target, testId: "nav-presupuesto", perm: "budgets:view" },
  { to: "/gastos/categorias", label: "Categorías gastos", icon: Layers, testId: "nav-gastos-categorias", perm: "expenses:edit" },
  { to: "/inventario/kardex", label: "Kardex", icon: BookOpen, testId: "nav-inventario-kardex", perm: "analytics:view" },
  { to: "/inventario/alertas", label: "Alertas stock", icon: AlertTriangle, testId: "nav-inventario-alertas", perm: "analytics:view" },
  { to: "/reportes/productividad", label: "Productividad técnicos", icon: Users, testId: "nav-reportes-productividad", perm: "analytics:view" },
  { to: "/reportes/historial", label: "Historial exportaciones", icon: History, testId: "nav-reportes-historial", perm: "report_exports:view" },
];

const PURCHASES_NAV = [
  { to: "/proveedores", label: "Proveedores", icon: Truck, testId: "nav-proveedores", perm: "suppliers:view" },
  { to: "/compras", label: "Órdenes de compra", icon: ClipboardList, testId: "nav-compras", perm: "purchase_orders:view" },
  { to: "/cuentas-por-pagar", label: "Cuentas por pagar", icon: Wallet, testId: "nav-cuentas-por-pagar", perm: "accounts_payable:view" },
  { to: "/pagos-proveedores", label: "Pagos proveedores", icon: Receipt, testId: "nav-pagos-proveedores", perm: "accounts_payable:view" },
  { to: "/devoluciones", label: "Devoluciones", icon: PackageX, testId: "nav-devoluciones", perm: "purchase_returns:view" },
  { to: "/documentos-proveedores", label: "Documentos proveedores", icon: FileText, testId: "nav-supplier-documents", perm: "supplier_documents:view" },
];

function NavItem({ to, label, icon: Icon, testId }) {
  const location = useLocation();
  const active = location.pathname.startsWith(to);
  return (
    <NavLink
      to={to}
      data-testid={testId}
      className={cn(
        "group flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium",
        "transition-colors duration-150",
        active
          ? "sidebar-active"
          : "text-slate-600 hover:text-slate-900 hover:bg-slate-50 border-l-[3px] border-transparent"
      )}
    >
      <Icon className="h-4 w-4 shrink-0" strokeWidth={2} />
      <span>{label}</span>
    </NavLink>
  );
}

function CollapsibleSection({ title, icon: Icon, items, hasPermission, testId }) {
  const location = useLocation();
  const visible = items.filter((r) => hasPermission(r.perm));
  const anyActive = visible.some((r) => location.pathname.startsWith(r.to));
  const [open, setOpen] = useState(anyActive);
  if (visible.length === 0) return null;
  return (
    <div className="space-y-0.5" data-testid={testId}>
      <button
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium",
          "text-slate-600 hover:text-slate-900 hover:bg-slate-50 border-l-[3px] border-transparent",
        )}
        data-testid={`${testId}-toggle`}
      >
        <Icon className="h-4 w-4 shrink-0" strokeWidth={2} />
        <span className="flex-1 text-left">{title}</span>
        <ChevronDown className={cn("h-4 w-4 transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div className="pl-4 space-y-0.5">
          {visible.map((r) => (
            <NavItem key={r.to} {...r} />
          ))}
        </div>
      )}
    </div>
  );
}

function ReportsSection({ hasPermission }) {
  return (
    <CollapsibleSection
      title="Reportes"
      icon={BarChart3}
      items={REPORTS_NAV}
      hasPermission={hasPermission}
      testId="nav-reports-section"
    />
  );
}

function PurchasesSection({ hasPermission }) {
  return (
    <CollapsibleSection
      title="Compras"
      icon={ShoppingCart}
      items={PURCHASES_NAV}
      hasPermission={hasPermission}
      testId="nav-purchases-section"
    />
  );
}

export function Sidebar({ mobileOpen, onCloseMobile }) {
  const { session, hasPermission } = useAuth();
  const isSA = session?.user?.is_super_admin;
  const operative = isOperative(session?.user);

  const content = (
    <div className="flex h-full flex-col bg-white border-r border-border">
      <div className="px-6 h-16 flex items-center gap-3 border-b border-border">
        <img
          src="/logo-principal.png"
          alt="TallerOS"
          data-testid="sidebar-brand-logo"
          className="h-10 w-10 rounded-md object-contain shadow-sm ring-1 ring-slate-200"
        />
        <div className="leading-tight">
          <div className="text-[15px] font-semibold tracking-tight">TallerOS</div>
          <div className="text-[11px] text-muted-foreground uppercase tracking-wider">
            by VRIntegrales
          </div>
        </div>
        <button
          data-testid="sidebar-close-mobile"
          onClick={onCloseMobile}
          className="ml-auto md:hidden text-muted-foreground hover:text-foreground"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-0.5" onClick={onCloseMobile}>
        {operative && <NavItem to="/mis-trabajos" label="Mis trabajos" icon={Wrench} testId="nav-mis-trabajos" />}
        {!operative && isSA && SUPER_ADMIN_NAV.map((item) => <NavItem key={item.to} {...item} />)}
        {!operative && NAV_TOP.map((item) => <NavItem key={item.to} {...item} />)}
        {!operative && <PurchasesSection hasPermission={hasPermission} />}
        {!operative && <ReportsSection hasPermission={hasPermission} />}
        {!operative && NAV_BOTTOM.filter((item) => (!item.ownerOnly || session?.user?.role_name === "dueno" || session?.user?.is_super_admin) && (!item.perm || hasPermission(item.perm))).map((item) => <NavItem key={item.to} {...item} />)}
      </nav>

      <div className="px-6 py-4 border-t border-border">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
          Etapa
        </div>
        <div className="text-xs text-slate-700 mt-1">Fase 8.2 · productividad</div>
      </div>
    </div>
  );

  return (
    <>
      <aside className="hidden md:flex md:w-64 md:shrink-0 md:h-screen md:sticky md:top-0 z-30">
        {content}
      </aside>
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-slate-900/50"
            onClick={onCloseMobile}
            data-testid="sidebar-backdrop"
          />
          <div className="absolute inset-y-0 left-0 w-64">{content}</div>
        </div>
      )}
    </>
  );
}

export function MobileMenuButton({ onClick }) {
  return (
    <button
      data-testid="sidebar-open-mobile"
      onClick={onClick}
      className="md:hidden inline-flex items-center justify-center h-9 w-9 rounded-md border border-border bg-white hover:bg-slate-50"
      aria-label="Abrir menú"
    >
      <Menu className="h-5 w-5" />
    </button>
  );
}
